import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { mutate } from "swr";
import { Switch } from "~/components/ui/switch";
import useToast from "~/components/ui/toast";
import { useFetcher } from "~/hooks/useFetcher";
import { useCanEditAgent } from "~/pages/manifold-nextgen/hooks/useCanEditAgent";
import {
  Agent,
  AgentScope,
  AgentToolkit,
  Toolkit,
  agentToolkitsPath,
  disableAgentToolkit,
  enableAgentToolkit,
  toolkitsPath,
} from "~/services/agents";
import { AgentTabHeader } from "./AgentTabHeader";

export const AgentToolkitsTab = () => {
  const agent = useOutletContext<Agent>();
  const canEdit = useCanEditAgent(agent);
  const { showSuccess, showError } = useToast();
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());

  const { data: toolkits, isLoading: toolkitsLoading } = useFetcher<Toolkit[]>(toolkitsPath());
  const { data: agentToolkits, isLoading: agentToolkitsLoading } = useFetcher<AgentToolkit[]>(
    agentToolkitsPath(agent.id)
  );

  const enabledIds = new Set((agentToolkits ?? []).map((t) => t.toolkit.id));
  const isLoading = toolkitsLoading || agentToolkitsLoading;
  const totalCount = toolkits?.length ?? 0;

  const handleToggle = async (toolkit: Toolkit, enabled: boolean) => {
    setTogglingIds((prev) => new Set(prev).add(toolkit.id));
    try {
      if (enabled) {
        await enableAgentToolkit(agent.id, toolkit.id);
        showSuccess(`"${toolkit.display_name}" Toolkit enabled`);
      } else {
        await disableAgentToolkit(agent.id, toolkit.id);
        showSuccess(`"${toolkit.display_name}" Toolkit disabled`);
      }
      await mutate(agentToolkitsPath(agent.id));
    } catch {
      showError(`Failed to ${enabled ? "enable" : "disable"} "${toolkit.display_name}" Toolkit`);
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(toolkit.id);
        return next;
      });
    }
  };

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-4">
      <AgentTabHeader
        title="Toolkits"
        isSystemAgent={agent.scope === AgentScope.SYSTEM}
        count={isLoading || totalCount === 0 ? undefined : enabledIds.size}
        max={isLoading || totalCount === 0 ? undefined : totalCount}
        context={isLoading ? undefined : "Tools the agent can use"}
        description="Toolkits expose tools the agent can call during a conversation. Mount the ones necessary for this agent to do its job."
      />

      {isLoading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-14 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      ) : !toolkits?.length ? (
        <p className="py-8 text-center text-sm text-muted-foreground">No toolkits available</p>
      ) : (
        <div className="flex flex-col divide-y divide-border rounded-lg border border-border">
          {toolkits.map((toolkit) => (
            <div key={toolkit.id} className="flex items-center justify-between px-4 py-3">
              <div className="min-w-0">
                <p className="text-sm font-medium">{toolkit.display_name}</p>
                {toolkit.description && (
                  <p className="text-xs text-muted-foreground">{toolkit.description}</p>
                )}
              </div>
              {togglingIds.has(toolkit.id) ? (
                <div className="flex h-[1.15rem] w-8 items-center justify-center">
                  <Loader2 className="size-3 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <Switch
                  checked={enabledIds.has(toolkit.id)}
                  onCheckedChange={(checked) => handleToggle(toolkit, checked)}
                  disabled={!canEdit}
                  aria-label={`${enabledIds.has(toolkit.id) ? "Disable" : "Enable"} ${toolkit.display_name}`}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
